import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import FormInputField from '../FormInputField';
import Button, { buttonType } from '../../components/Button';
import Loader from '../Loader';
import { useLabels } from '../../hooks';
import { UserContext } from '../../contexts';
import { userApi } from '../../api';
import styles from './LoginForm.module.scss';

const SetNewPassword = ({
    confirmPasswordUsername,
    verifiedCode,
    smsSentRef,
    voiceCallSentRef,
    setViewState = () => {},
    setNewPasswordRequiredData = () => {},
}) => {
    const [showLoader, setShowLoader] = useState(false);
    const [error, setError] = useState();
    const [passwordValue, setPasswordValue] = useState('');
    const { getLabel, getCognitoApiError } = useLabels();
    const { confirmPassword, signIn } = useContext(UserContext);
    const isActive = useRef(false);
    const newPasswordRef = useRef();
    const confirmPasswordRef = useRef();

    useEffect(() => {
        isActive.current = true;
        return () => { isActive.current = false; };
    }, []);

    const meetsLength = passwordValue.length >= 8;
    const meetsLowercase = /[a-z]/.test(passwordValue);
    const meetsUppercase = /[A-Z]/.test(passwordValue);
    const meetsNumber = /[0-9]/.test(passwordValue);
    const meetsSymbol = /[-_^$*.{}()?"!@#%&/[\],><':;|~`+=\\]/.test(passwordValue);

    const confirmPasswordValidation = useCallback((value) => {
        return value === newPasswordRef.current.getValue() ? '' : getLabel('login_form_confirm_new_password_match_error');
    }, [getLabel]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        let focusField = null;

        if (!newPasswordRef.current.validation()) {
            focusField = focusField || newPasswordRef.current;
        }
        if (!confirmPasswordRef.current.validation()) {
            focusField = focusField || confirmPasswordRef.current;
        }
        if (focusField) {
            focusField.focus();
            return;
        }

        setError(null);
        setShowLoader(true);

        const newPassword = newPasswordRef.current.getValue();

        const handleSignInError = (err) => {
            if (!isActive.current) return;
            switch (err.code) {
                case 'HandleNewPasswordRequired':
                    setNewPasswordRequiredData(err);
                    setViewState('new_password_required');
                    break;
                case 'PasswordResetRequiredException':
                    setViewState('reset_password');
                    break;
                default:
                    setError(err);
            }
            setShowLoader(false);
        };

        if (smsSentRef.current || voiceCallSentRef.current) {
            userApi.VerifySmsOtpAndResetPassword(confirmPasswordUsername, verifiedCode, newPassword)
                .then(res => {
                    if (!res?.success) throw new Error(getLabel('otp_expired_or_invalid'));
                    return signIn(confirmPasswordUsername, newPassword);
                })
                .then(() => {
                    if (isActive.current) setShowLoader(false);
                })
                .catch(handleSignInError);
            return;
        }

        confirmPassword(confirmPasswordUsername, verifiedCode, newPassword)
            .then(() => {
                if (!isActive.current) return;
                return signIn(confirmPasswordUsername, newPassword)
                    .then(() => { if (isActive.current) setShowLoader(false); })
                    .catch(handleSignInError);
            })
            .catch(err => {
                if (isActive.current) { setError(err); setShowLoader(false); }
            });
    }, [confirmPasswordUsername, verifiedCode, confirmPassword, signIn, setNewPasswordRequiredData, setViewState, getLabel]);

    return (
        <>
            <div className={styles.login}>
                <div className={styles.content}>
                    <div>
                        <div className={styles.stepProgress}>
                            <div className={styles.stepLabel}>{getLabel('set_new_password_step')}</div>
                            <div className={styles.progressBar}>
                                <div className={styles.progressFill} style={{ width: '100%' }} />
                            </div>
                        </div>
                        <div className={styles.stepTitle}>{getLabel('set_new_password_title')}</div>
                        <div className={styles.text}>{getLabel('set_new_password_subtitle')}</div>
                        <form className={styles.fields} onSubmit={handleSubmit}>
                            <div>
                                <FormInputField
                                    label={getLabel('login_form_confirm_new_password_field')}
                                    placeholder={getLabel('login_form_confirm_new_password_field_placeholder')}
                                    ref={newPasswordRef}
                                    passwordValidation={true}
                                    type="password"
                                    heContentDirection="ltr"
                                    hePlaceholderDirection="rtl"
                                    onChange={e => setPasswordValue(e.target.value)}
                                />
                                <div className={styles.passwordRequirements}>
                                    <div className={styles.requirementsTitle}>
                                        {getLabel('password_requirements_title')}
                                    </div>
                                    <div className={[styles.requirementItem, meetsLength ? styles.requirementItemMet : ''].join(' ')}>
                                        {getLabel('password_req_min_chars')}
                                    </div>
                                    <div className={[styles.requirementItem, meetsLowercase ? styles.requirementItemMet : ''].join(' ')}>
                                        {getLabel('password_req_lowercase')}
                                    </div>
                                    <div className={[styles.requirementItem, meetsUppercase ? styles.requirementItemMet : ''].join(' ')}>
                                        {getLabel('password_req_uppercase')}
                                    </div>
                                    <div className={[styles.requirementItem, meetsNumber ? styles.requirementItemMet : ''].join(' ')}>
                                        {getLabel('password_req_number')}
                                    </div>
                                    <div className={[styles.requirementItem, meetsSymbol ? styles.requirementItemMet : ''].join(' ')}>
                                        {getLabel('password_req_symbol')}
                                    </div>
                                </div>
                            </div>
                            <FormInputField
                                label={getLabel('login_form_confirm_again_new_password_field')}
                                placeholder={getLabel('login_form_confirm_again_new_password_field_placeholder')}
                                ref={confirmPasswordRef}
                                validationPattern={confirmPasswordValidation}
                                type="password"
                                heContentDirection="ltr"
                                hePlaceholderDirection="rtl"
                            />
                            <button hidden={true} />
                        </form>
                    </div>
                    <div className={styles.buttons}>
                        <div>
                            <div className={styles.error} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error) }} />
                            <Button type={buttonType.premium} className={styles.main_button} onClick={handleSubmit}>
                                {getLabel('login_form_confirm_change_password_button')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {showLoader && <Loader />}
        </>
    );
};

export default SetNewPassword;
